import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  TextInput, 
  toast
} from '@etherisc/ui-kit';
import { PositiveButton, NeutralButton } from '../buttons';
import { FormCard } from '../cards';
import { validatePhoneMultiCountry, getSupportedCountries, getCountryConfig } from '@myubuntu/phone-validation';
import { validateCoordinates } from '../../utils/coordinateValidation';
import { farmerService } from '../../services/farmerService';
import { useAuth } from '../../hooks/useAuth';
import {
  CountrySelector,
  AdministrativeLevels,
  AgriculturalActivities,
  PaymentMethods,
  DocumentUpload,
} from './index';
import { FarmerOrganizationManagement } from './FarmerOrganizationManagement';
import { FarmerLocationMap } from './FarmerLocationMap';
import type { Farmer, FarmerFormData } from '../../types/farmer';

// Comprehensive Zod schema for farmer forms (SHARED between Create and Edit)
const validationSchema = z.object({
  // Basic Information
  name: z.string().min(1, 'Farmer name is required')
    .refine((val) => !/\d/.test(val), { message: 'Farmer name must not contain numbers' }),
  phone: z.string()
    .min(1, 'Phone number is required')
    .refine((val) => {
      // Use all supported countries from shared config
      const phoneResult = validatePhoneMultiCountry(val, undefined, { mobileOnly: true });
      return phoneResult.isValid;
    }, {
      message: (() => {
        // Build error message dynamically from shared config
        const examples = getSupportedCountries()
          .map(code => {
            const config = getCountryConfig(code);
            return config ? `${config.countryCode}: ${config.example}` : '';
          })
          .filter(Boolean)
          .join(', ');
        return `Please enter a valid mobile number. Supported formats: ${examples}`;
      })()
    }),
  email: z.string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    }, 'Please enter a valid email address'),
  nationalId: z.string().optional(),
    
  // Location Information
  countryCode: z.string().min(1, 'Country selection is required'),
  administrativeLevels: z.record(z.string())
    .superRefine((levels, ctx) => {
      // Validate each required level individually for better error mapping
      if (!levels['1'] || levels['1'].trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['1'],
          message: 'Province is required'
        });
      }
      if (!levels['2'] || levels['2'].trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['2'], 
          message: 'District is required'
        });
      }
      if (!levels['3'] || levels['3'].trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['3'],
          message: 'Ward is required'
        });
      }
    }),
  
  // Coordinates (Optional)
  latitude: z.union([z.number(), z.string()])
    .optional()
    .refine((val) => {
      if (val === undefined || val === null || val === '') return true;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return !isNaN(num);
    }, 'Please enter a valid number for latitude')
    .transform((val) => {
      if (val === undefined || val === null || val === '') return undefined;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num) ? undefined : num;
    })
    .refine((val) => {
      if (val === undefined || val === null) return true;
      return val >= -90 && val <= 90;
    }, 'Latitude must be between -90 and 90'),
  longitude: z.union([z.number(), z.string()])
    .optional()
    .refine((val) => {
      if (val === undefined || val === null || val === '') return true;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return !isNaN(num);
    }, 'Please enter a valid number for longitude')
    .transform((val) => {
      if (val === undefined || val === null || val === '') return undefined;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num) ? undefined : num;
    })
    .refine((val) => {
      if (val === undefined || val === null) return true;
      return val >= -180 && val <= 180;
    }, 'Longitude must be between -180 and 180'),
  
  // Farmer Location (Conditional)
  hasSeparateFarmerLocation: z.boolean().optional(),
  farmerLocation: z.object({
    administrativeLevels: z.record(z.string()).optional(),
    village: z.string().optional(),
  }).optional(),
  
  // Agricultural Information
  primaryActivity: z.string().optional(),
  otherActivities: z.array(z.string()).optional(),
  area: z.union([z.number(), z.string()])
    .optional()
    .transform((val) => {
      if (val === undefined || val === null || val === '') return undefined;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num) ? undefined : num;
    })
    .refine((val) => {
      if (val === undefined || val === null) return true; // Now optional
      return val > 0;
    }, 'Farm area must be greater than 0 if provided'),
  
  herdSize: z.union([z.number(), z.string()])
    .optional()
    .transform((val) => {
      if (val === undefined || val === null || val === '') return undefined;
      const num = typeof val === 'string' ? parseInt(val, 10) : val;
      return isNaN(num) ? undefined : num;
    })
    .refine((val) => {
      if (val === undefined || val === null) return true;
      return val >= 0 && Number.isInteger(val);
    }, 'Herd size must be a non-negative integer if provided'),
  
  // Payment Methods (Dynamic Array)
  paymentDetails: z.array(z.any()).optional(),
  
  // Documents (Dynamic Array)
  proofOfStake: z.array(z.any()).optional(),
  
  // Organization Management
  organizationId: z.string().uuid('Organization is required'),
  organizationIds: z.array(z.string()).optional(),
});

interface FarmerFormProps {
  mode: 'create' | 'edit';
  initialData?: Farmer;
  onSubmit: (data: FarmerFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  title?: string;
  subtitle?: string;
}

export function FarmerForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  title,
  subtitle
}: FarmerFormProps) {
  const { user } = useAuth();
  
  // React Hook Form setup with comprehensive schema (IDENTICAL logic)
  const { 
    register, 
    control,
    handleSubmit, 
    formState: { errors }, 
    reset,
    watch, 
    setValue
  } = useForm<FarmerFormData>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      nationalId: '',
      countryCode: 'ZW',
      administrativeLevels: {},
      latitude: undefined,
      longitude: undefined,
      hasSeparateFarmerLocation: false,
      farmerLocation: {
        administrativeLevels: {},
        village: '',
      },
      primaryActivity: '',
      otherActivities: [],
      area: undefined,
      herdSize: undefined,
      paymentDetails: [],
      proofOfStake: [],
      organizationId: user?.organization?.id || '',
      organizationIds: [],
    }
  });

  const countryCode = useWatch({ control, name: 'countryCode' });
  const hasSeparateFarmerLocation = useWatch({ control, name: 'hasSeparateFarmerLocation' });
  const watchedLatitude = useWatch({ control, name: 'latitude' });
  const watchedLongitude = useWatch({ control, name: 'longitude' });
  const watchedAdminLevels = useWatch({ control, name: 'administrativeLevels' });

  const [boundaryGeometry, setBoundaryGeometry] = useState<GeoJSON.Geometry | undefined>();

  // Fetch boundary geometry for the smallest selected admin level
  useEffect(() => {
    const levels = watchedAdminLevels as Record<string, string> | undefined;
    if (!levels) {
      setBoundaryGeometry(undefined);
      return;
    }

    const smallestCode = levels['3'] || levels['2'] || levels['1'];
    if (!smallestCode) {
      setBoundaryGeometry(undefined);
      return;
    }

    let cancelled = false;
    farmerService.getAdminBoundaryGeometry(smallestCode).then(result => {
      if (cancelled) return;
      setBoundaryGeometry(result?.geometry);
    }).catch(() => {
      if (!cancelled) setBoundaryGeometry(undefined);
    });

    return () => { cancelled = true; };
  }, [watchedAdminLevels]);

  const handleMapCoordinateChange = useCallback((lat: number | undefined, lng: number | undefined) => {
    setValue('latitude', lat, { shouldValidate: true, shouldDirty: true });
    setValue('longitude', lng, { shouldValidate: true, shouldDirty: true });
  }, [setValue]);

  // Load initial data for edit mode (IDENTICAL to EditFarmer logic)
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      reset({
        name: initialData.name,
        phone: initialData.phone,
        email: initialData.email || '',
        nationalId: initialData.nationalId || '',
        countryCode: initialData.countryCode || 'ZW',
        administrativeLevels: (() => {
          // Handle schema-wrapped format: {data: {}, schemaId: "...", schemaVersion: "..."}
          if (initialData.administrativeLevels && typeof initialData.administrativeLevels === 'object' && 'data' in initialData.administrativeLevels) {
            const schemaWrapped = initialData.administrativeLevels as { data: unknown };
            return typeof schemaWrapped.data === 'object' && schemaWrapped.data !== null ? schemaWrapped.data as Record<string, string> : {};
          }
          // Handle direct object format (fallback)
          return typeof initialData.administrativeLevels === 'object' && initialData.administrativeLevels !== null ? initialData.administrativeLevels as Record<string, string> : {};
        })(),
        latitude: initialData.latitude || undefined,
        longitude: initialData.longitude || undefined,
        hasSeparateFarmerLocation: initialData.hasSeparateFarmerLocation || false,
        farmerLocation: initialData.farmerLocation ? {
          administrativeLevels: (() => {
            const farmAdminLevels = initialData.farmerLocation?.administrativeLevels;
            if (farmAdminLevels && typeof farmAdminLevels === 'object' && 'data' in farmAdminLevels) {
              const schemaWrapped = farmAdminLevels as { data: unknown };
              return typeof schemaWrapped.data === 'object' && schemaWrapped.data !== null ? schemaWrapped.data as Record<string, string> : {};
            }
            return typeof farmAdminLevels === 'object' && farmAdminLevels !== null ? farmAdminLevels as Record<string, string> : {};
          })(),
          village: initialData.farmerLocation.village || '',
        } : {
          administrativeLevels: {},
          village: '',
        },
        primaryActivity: initialData.primaryActivity || '',
        otherActivities: (() => {
          // Handle schema-wrapped format: {data: [], schemaId: "...", schemaVersion: "..."}
          if (initialData.otherActivities && typeof initialData.otherActivities === 'object' && 'data' in initialData.otherActivities) {
            const schemaWrapped = initialData.otherActivities as { data: unknown };
            return Array.isArray(schemaWrapped.data) ? schemaWrapped.data : [];
          }
          // Handle direct array format
          return Array.isArray(initialData.otherActivities) ? initialData.otherActivities : [];
        })(),
        area: initialData.area || undefined,
        paymentDetails: (() => {
          // Handle schema-wrapped format for paymentDetails
          if (initialData.paymentDetails && typeof initialData.paymentDetails === 'object' && 'data' in initialData.paymentDetails) {
            const schemaWrapped = initialData.paymentDetails as { data: unknown };
            return Array.isArray(schemaWrapped.data) ? schemaWrapped.data : [];
          }
          return Array.isArray(initialData.paymentDetails) ? initialData.paymentDetails : [];
        })(),
        proofOfStake: (() => {
          // Handle schema-wrapped format for proofOfStake
          if (initialData.proofOfStake && typeof initialData.proofOfStake === 'object' && 'data' in initialData.proofOfStake) {
            const schemaWrapped = initialData.proofOfStake as { data: unknown };
            return Array.isArray(schemaWrapped.data) ? schemaWrapped.data : [];
          }
          return Array.isArray(initialData.proofOfStake) ? initialData.proofOfStake : [];
        })(),
        organizationId: initialData.organizationId || '',
        organizationIds: (() => {
          // Handle schema-wrapped format for organizationIds
          if (initialData.organizationIds && typeof initialData.organizationIds === 'object' && 'data' in initialData.organizationIds) {
            const schemaWrapped = initialData.organizationIds as { data: unknown };
            return Array.isArray(schemaWrapped.data) ? schemaWrapped.data : [];
          }
          return Array.isArray(initialData.organizationIds) ? initialData.organizationIds : [];
        })(),
      });
    }
  }, [mode, initialData, reset]);

  // Convert React Hook Form errors to component-expected format (IDENTICAL to EditFarmer)
  const getErrorMessages = useCallback((errors: Record<string, unknown>): Record<string, string> => {
    const errorMessages: Record<string, string> = {};
    
    const extractErrors = (obj: Record<string, unknown>, prefix = '') => {
      Object.keys(obj || {}).forEach(key => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const error = obj[key];
        
        if (error && typeof error === 'object' && 'message' in error) {
          errorMessages[fullKey] = (error as { message: string }).message;
          
          // Map administrative levels to component-expected keys
          const errorMessage = (error as { message: string }).message;
          if (fullKey === 'administrativeLevels.1') {
            errorMessages['level_1'] = errorMessage;
          } else if (fullKey === 'administrativeLevels.2') {
            errorMessages['level_2'] = errorMessage;
          } else if (fullKey === 'administrativeLevels.3') {
            errorMessages['level_3'] = errorMessage;
          }
          
          // Map payment method errors
          if (fullKey.startsWith('paymentDetails.')) {
            const match = fullKey.match(/paymentDetails\.(\d+)\.(.+)/);
            if (match) {
              const [, index, fieldName] = match;
              errorMessages[`payment_${index}_${fieldName}`] = errorMessage;
            }
          }
        } else if (typeof error === 'object' && error !== null) {
          extractErrors(error as Record<string, unknown>, fullKey);
        }
      });
    };
    
    extractErrors(errors);
    return errorMessages;
  }, []);

  // Form submission handler with proper data preparation
  const handleFormSubmit = useCallback(async (data: FarmerFormData) => {
    // Handle numeric fields with proper type conversion and formatting
    const coordinateValidation = validateCoordinates(data.latitude, data.longitude);
    const numericLatitude = coordinateValidation.latitude.formatted;
    const numericLongitude = coordinateValidation.longitude.formatted;
    
    const numericArea = typeof data.area === 'string' && data.area !== '' 
      ? parseFloat(data.area) 
      : data.area;

    // Ensure paymentDetails is an array before processing
    const paymentDetailsArray = Array.isArray(data.paymentDetails) ? data.paymentDetails : [];
    
    // Normalize phone numbers in payment methods for backend compatibility
    const normalizedPaymentDetails = paymentDetailsArray.map(payment => {
      if (payment.type === 'mobile_money' && payment.registered_mobile_number) {
        return {
          ...payment,
          registered_mobile_number: (payment.registered_mobile_number as string).replace(/[\s\-().]/g, '')
        };
      }
      return payment;
    });

    // Prepare data with schema wrapping for ALL JSON fields
    const submitData: Record<string, unknown> = {
      name: data.name.trim(),
      phone: data.phone.trim(),
      email: data.email?.trim() || undefined,

      // Country and location data
      countryCode: data.countryCode,
      administrativeLevels: Object.keys(data.administrativeLevels || {}).length > 0 ? {
        schemaId: 'farmers.administrativeLevels',
        schemaVersion: '1.1.0',
        data: data.administrativeLevels
      } : undefined,
      latitude: numericLatitude || undefined,
      longitude: numericLongitude || undefined,

      // Separate farmer location data - wrap in schema format
      hasSeparateFarmerLocation: data.hasSeparateFarmerLocation || false,
      farmerLocation: data.hasSeparateFarmerLocation && data.farmerLocation ? {
        schemaId: 'farmers.farmerLocation',
        schemaVersion: '1.0.0',
        data: {
          administrativeLevels: data.farmerLocation?.administrativeLevels || undefined,
          village: data.farmerLocation?.village?.trim() || undefined,
        }
      } : undefined,

      // Agricultural data
      primaryActivity: data.primaryActivity?.trim() || undefined,
      otherActivities: data.otherActivities ? {
        schemaId: 'farmers.otherActivities',
        schemaVersion: '1.1.0',
        data: data.otherActivities
      } : undefined,
      area: numericArea || undefined,

      // Payment and documentation - wrap in schema format
      paymentDetails: normalizedPaymentDetails.length > 0 ? {
        schemaId: 'farmers.paymentDetails',
        schemaVersion: '1.1.0',
        data: normalizedPaymentDetails
      } : undefined,
      proofOfStake: data.proofOfStake && data.proofOfStake.length > 0 ? {
        schemaId: 'farmers.proofOfStake',
        schemaVersion: '1.0.0',
        data: data.proofOfStake
      } : undefined,

      // Organization
      organizationId: data.organizationId,
      organizationIds: data.organizationIds && data.organizationIds.length > 0 ? {
        schemaId: 'farmers.organizationIds',
        schemaVersion: '1.0.0',
        data: data.organizationIds
      } : undefined,
    };

    // Call the parent's onSubmit with prepared data
    await onSubmit(submitData as unknown as FarmerFormData);
  }, [onSubmit]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-md"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {title || (mode === 'create' ? 'Create Farmer' : 'Edit Farmer')}
          </h1>
          <p className="text-gray-600 mt-1">
            {subtitle || (mode === 'create' ? 'Add a new farmer to the system' : 'Update farmer information')}
          </p>
        </div>
      </div>

      {/* Validation Error Display */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="text-red-800 font-semibold mb-2">Please fix the following errors:</h3>
          <ul className="text-red-700 space-y-1">
            {Object.entries(getErrorMessages(errors)).map(([field, message]) => (
              <li key={field} className="text-sm">
                • <strong>{field}</strong>: {message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Form */}
      <FormCard title="Farmer Information">
        <form onSubmit={handleSubmit(handleFormSubmit, (validationErrors) => {
          const converted = getErrorMessages(validationErrors);
          const errorList = Object.entries(converted)
            .map(([field, message]) => `• ${field}: ${message}`)
            .join('\n');
          
          toast.error('Validation Error', { description: `Please fix these fields:\n${errorList}` });
        })} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextInput
              label="Farmer Name *"
              placeholder="Enter farmer's full name"
              {...register('name')}
              error={errors.name?.message}
            />
            <TextInput
              label="Phone Number *"
              placeholder="+263 XX XXX XXXX"
              {...register('phone')}
              error={errors.phone?.message}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextInput
              label="Email Address"
              placeholder="farmer@example.com"
              type="email"
              {...register('email')}
              error={errors.email?.message}
            />
            <TextInput
              label="National ID Number"
              placeholder="63-123456-A78"
              {...register('nationalId')}
              error={errors.nationalId?.message}
            />
          </div>

          {/* Country Selection */}
          <div className="border-t pt-6">
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <CountrySelector
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isSubmitting}
                  error={errors.countryCode?.message}
                />
              )}
            />
          </div>

          {/* Administrative Levels */}
          <div className="border-t pt-6">
            <Controller
              name="administrativeLevels"
              control={control}
              render={({ field }) => (
                <AdministrativeLevels
                  countryCode={countryCode}
                  values={field.value || {}}
                  onChange={field.onChange}
                  disabled={isSubmitting}
                  errors={getErrorMessages(errors)}
                />
              )}
            />

            {/* Coordinates - Keep for climate integration */}
            <div className="mt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Coordinates (Optional)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  label="Latitude"
                  placeholder="-18.5"
                  type="number"
                  step="any"
                  {...register('latitude')}
                  error={errors.latitude?.message}
                />
                <TextInput
                  label="Longitude"
                  placeholder="29.2"
                  type="number"
                  step="any"
                  {...register('longitude')}
                  error={errors.longitude?.message}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {boundaryGeometry
                  ? 'Click within the highlighted boundary to set coordinates. Changes are saved when you press Save.'
                  : 'Click on the map to set coordinates, or type them above. Select an administrative unit to show its boundary.'}
              </p>
              <div className="mt-3">
                <FarmerLocationMap
                  latitude={typeof watchedLatitude === 'number' ? watchedLatitude : undefined}
                  longitude={typeof watchedLongitude === 'number' ? watchedLongitude : undefined}
                  onCoordinateChange={handleMapCoordinateChange}
                  boundaryGeometry={boundaryGeometry}
                />
              </div>
            </div>
          </div>

          {/* Separate Farmer Address */}
          <div className="border-t pt-6">
            <div className="flex items-center gap-3 mb-4">
              <Controller
                name="hasSeparateFarmerLocation"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="separateFarmerLocation"
                    checked={field.value || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      field.onChange(checked);
                      if (!checked) {
                        setValue('farmerLocation', {
                          administrativeLevels: {},
                          village: '',
                        });
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                )}
              />
              <label htmlFor="separateFarmerLocation" className="text-lg font-medium text-gray-900">
                Separate Farmer Address
              </label>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Check this box if the farmer's residential address is different from the farm location
            </p>

            {hasSeparateFarmerLocation && (
              <div className="bg-gray-50 p-6 rounded-lg border">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Farmer Address Details</h4>
                
                <Controller
                  name="farmerLocation"
                  control={control}
                  render={({ field }) => {
                    const farmerLocation = field.value || {};
                    
                    const updateFarmerLocation = (updates: Partial<typeof farmerLocation>) => {
                      field.onChange({ ...farmerLocation, ...updates });
                    };
                    
                    return (
                        <div className="mb-6">
                          <AdministrativeLevels
                            countryCode={countryCode}
                            values={farmerLocation.administrativeLevels || {}}
                            onChange={(levels) => updateFarmerLocation({ administrativeLevels: levels })}
                            disabled={isSubmitting}
                            errors={getErrorMessages(errors)}
                          />
                        </div>
                    );
                  }}
                />
              </div>
            )}
          </div>

          {/* Agricultural Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4">Agricultural Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <TextInput
                label="Farm Area (hectares)"
                placeholder="2.5"
                {...register('area', { 
                  setValueAs: (value) => value === '' ? undefined : parseFloat(value) 
                })}
                error={errors.area?.message}
              />
              <TextInput
                label="Herd Size"
                placeholder="10"
                type="number"
                {...register('herdSize', { 
                  setValueAs: (value) => value === '' ? undefined : parseInt(value, 10)
                })}
                error={errors.herdSize?.message}
              />
            </div>
            
            <Controller
              name="primaryActivity"
              control={control}
              render={({ field: primaryField }) => (
                <Controller
                  name="otherActivities"
                  control={control}
                  render={({ field: otherField }) => (
                    <AgriculturalActivities
                      countryCode={countryCode}
                      primaryActivity={primaryField.value || ''}
                      otherActivities={otherField.value || []}
                      onPrimaryChange={primaryField.onChange}
                      onOtherChange={otherField.onChange}
                      disabled={isSubmitting}
                      errors={getErrorMessages(errors)}
                    />
                  )}
                />
              )}
            />
          </div>

          {/* Payment Methods */}
          <div className="border-t pt-6">
            <Controller
              name="paymentDetails"
              control={control}
              render={({ field }) => (
                <PaymentMethods
                  countryCode={countryCode}
                  paymentMethods={field.value || []}
                  onChange={field.onChange}
                  disabled={isSubmitting}
                  errors={getErrorMessages(errors)}
                  farmerData={{
                    name: watch('name'),
                    phone: watch('phone')
                  }}
                />
              )}
            />
          </div>

          {/* Document Upload */}
          <div className="border-t pt-6">
            <Controller
              name="proofOfStake"
              control={control}
              render={({ field }) => (
                <DocumentUpload
                  countryCode={countryCode}
                  documents={field.value || []}
                  onChange={field.onChange}
                  disabled={isSubmitting}
                  errors={getErrorMessages(errors)}
                  farmerId={mode === 'edit' ? initialData?.id : undefined}
                />
              )}
            />
          </div>

          {/* Organization Management */}
          <div className="border-t pt-6">
            <Controller
              name="organizationIds"
              control={control}
              render={({ field }) => (
                <FarmerOrganizationManagement
                  primaryOrganizationId={watch('organizationId') || ''}
                  additionalOrganizationIds={field.value || []}
                  onChange={field.onChange}
                  disabled={isSubmitting}
                  errors={getErrorMessages(errors)}
                />
              )}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <NeutralButton
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </NeutralButton>
            <PositiveButton
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              loadingText={mode === 'create' ? 'Creating...' : 'Updating...'}
              showIcon={false}
            >
              {mode === 'create' ? 'Create Farmer' : 'Update Farmer'}
            </PositiveButton>
          </div>
        </form>
      </FormCard>
    </div>
  );
}
